/**
 * English - `comms`: the Email templates screen and its editor.
 *
 * Keys: `page.*` for the screen's header and banners; `empty.*`, `table.*`,
 * `row.*` and `actions.*` for the list; `preview.*` for the read-only preview
 * dialog; `test.*` for mailing one to yourself; `reset.*` for the
 * restore-the-default confirmation; `fields.*` for form labels; `editor.*` for
 * the editing dialog. Template names,
 * descriptions, subjects, bodies and placeholder explanations come from the
 * server and are not translated here; `{name}` is a template's name.
 */
const comms: Record<string, string> = {
  'page.title': 'Email templates',
  'page.subtitle':
    'What your shop says to your customers, in your words. Edit any of these, switch one off if you would rather say nothing, and restore the original at any time — the version that ships with the platform is always underneath your own.',
  'page.action_failed': 'That did not work. Please try again.',
  'page.load_failed': 'Could not load your emails.',

  'empty.title': 'No emails are configured',
  'empty.description': 'Nothing to show yet. This is unexpected — please refresh the page.',

  'search_label': 'Search emails',
  'search_placeholder': 'Search by name, subject or event',
  'select_row': 'Select {name}',

  'table.format': 'Format',
  'format.designed': 'Designed',
  'format.plain': 'Plain text',

  'filters.wording': 'Wording',
  'filters.any_event': 'Any email',

  'sort.catalogue': 'Shop order',
  'sort.name_asc': 'Name A–Z',
  'sort.name_desc': 'Name Z–A',
  'sort.updated_desc': 'Recently changed',
  'sort.status': 'Sending first',

  'kpi.total': 'Emails',
  'kpi.sending': 'Being sent',
  'kpi.sending_hint': 'Customers receive these',
  'kpi.silent': 'Switched off',
  'kpi.silent_hint': 'Nobody is told',
  'kpi.reworded': 'In your words',
  'kpi.reworded_hint': 'Changed from the default',

  'actions.compare': 'Compare with default',

  'compare.title': '{name}: yours and the default',
  'compare.yours': 'What your shop sends',
  'compare.shipped': 'What the platform ships',
  'compare.unchanged': 'You have not changed this email, so it sends exactly the wording below.',

  'bulk.deactivate_title': 'Switch emails off',
  'bulk.deactivate_message_one':
    'Switch off {count} email? Customers will stop being told when it happens.',
  'bulk.deactivate_message_other':
    'Switch off {count} emails? Customers will stop being told when these happen.',

  'toast.activated': 'This email is being sent again.',
  'toast.deactivated': 'This email is switched off.',
  'toast.reset': 'Restored the wording this email ships with.',
  'toast.bulk_activated_one': '{count} email switched on.',
  'toast.bulk_activated_other': '{count} emails switched on.',
  'toast.bulk_deactivated_one': '{count} email switched off.',
  'toast.bulk_deactivated_other': '{count} emails switched off.',
  'toast.bulk_partial': '{done} updated, {failed} could not be.',

  'table.event': 'Event',
  'table.description': 'Description',
  'table.status': 'Status',
  'table.last_updated': 'Last updated',

  'row.your_wording': 'Your wording',
  'row.default_wording': 'Default wording',
  'row.designed': 'Designed — your own layout',
  'row.never_changed': 'Never — using the default',

  'actions.menu_label': 'Actions for {name}',
  'actions.view_wording': 'View wording',
  'actions.activate': 'Activate',
  'actions.deactivate': 'Deactivate',
  'actions.reset': 'Reset to default',

  'preview.title': 'Preview: {name}',
  'preview.render_failed': 'Could not render this email.',
  'preview.sample_note':
    "Filled in with a sample order and customer. The shop's name, address and your own details are the real ones.",

  'test.menu': 'Send test email',
  'test.send_short': 'Send test',
  'test.title': 'Send a test: {name}',
  'test.about':
    'We will email you the {name} filled in with the same sample order and customer the preview uses, so you can read it in a real inbox — its layout, its spacing, how it wraps — before a customer does.',
  'test.draft_note':
    'Sends the wording in this editor, including changes you have not saved yet.',
  'test.saved_note': 'Sends the wording currently in force for this email.',
  'test.event_off':
    'This email is switched off, so no customer receives it. The test still comes to you — reading it is what you would do before switching it on.',
  'test.recipient': 'Send to',
  'test.recipient_hint': 'Your own address, usually. Nothing goes to your customers.',
  'test.recipient_invalid': 'That does not look like an email address.',
  'test.send': 'Send test email',
  'test.sending': 'Sending…',
  'test.sent': 'Sent to {recipient}.',
  'test.look_for': 'It arrives with the subject {subject}.',
  'test.check_inbox': 'It can take a minute. Check the spam folder if it does not turn up.',
  'test.not_sent': 'The test email was not sent',
  'test.failed_generic': 'Could not send the test email. Please try again in a moment.',
  'test.send_another': 'Send another',
  'test.try_again': 'Try again',

  'reset.title': 'Restore the default {name}?',
  'reset.confirm': 'Restore the default',
  'reset.restoring': 'Restoring…',
  'reset.body':
    'Your own wording for this email will be discarded and the version that ships with the platform used instead. This also switches the email back on.',

  'fields.subject': 'Subject',
  'fields.body': 'Body',

  'editor.placeholders_title': 'Placeholders you can use',
  'editor.placeholders_hint':
    'Click one to drop it into the body where your cursor is. Each is replaced with the real value when the email is sent.',
  'editor.placeholders_hint_design':
    'Drop one of these into any block’s text. Each is replaced with the real value when the email is sent.',
  'editor.required': 'required',
  'editor.problem_required': '{name} is required',
  'editor.rendering': 'Rendering…',
  'editor.sending_on': 'This email is being sent',
  'editor.sending_off': 'This email is switched off',
  'editor.sending_on_hint': 'Customers receive it when this event happens in your shop.',
  'editor.sending_off_hint': 'Nobody receives it. Your wording is kept for when you switch it back on.',
  'editor.toggle_label': 'Send the {name} email',
  'editor.preview_title': 'Preview, with sample values',
  'editor.preview_hint':
    'Press {button} to see this email filled in with a sample order and customer, exactly as it will read.',
  'editor.cannot_fill':
    "This email cannot fill in {names}. Saving it will be refused — the text would be printed as written into a customer's inbox.",
  'editor.own_wording': 'This is your own wording. The version {name} ships with is still underneath it.',
  'editor.save_failed': 'Could not save that.',
  'editor.read_only':
    'You can read these emails but not change them. Ask an administrator for permission to manage the store.',
};

export default comms;
